import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { uploadFile, uploadVideoChunked } from "@/lib/upload";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Download, FileText, Paperclip, Send, MessageCircle, X } from "lucide-react";

const Attachment = ({ file, mine }) => {
  const isImage = (file.content_type || "").startsWith("image/");
  if (isImage) {
    return (
      <a href={mediaUrl(file.url)} target="_blank" rel="noreferrer" data-testid={`chat-att-${file.id}`}>
        <img src={mediaUrl(file.url)} alt={file.name} className="rounded-xl max-h-56 w-auto border border-border/40" />
      </a>
    );
  }
  return (
    <a
      href={mediaUrl(file.url)}
      target="_blank"
      rel="noreferrer"
      data-testid={`chat-att-${file.id}`}
      className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-xs ${mine ? "bg-primary-foreground/15" : "bg-background border border-border"}`}
    >
      <FileText size={15} className="shrink-0" />
      <span className="truncate max-w-[12rem]">{file.name}</span>
      <Download size={13} className="shrink-0 opacity-70" />
    </a>
  );
};

export default function Chat() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState("");
  const scrollerRef = useRef(null);

  const isAdmin = user?.role === "admin";

  const loadThreads = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/chat/threads");
      setThreads(data);
    } catch (e) {
      console.error("loadThreads failed:", e);
    }
  }, [isAdmin]);

  const loadMessages = useCallback(async (withUser) => {
    const params = isAdmin && withUser ? { with_user: withUser } : {};
    try {
      const { data } = await api.get("/chat/messages", { params });
      setMsgs(data);
      setTimeout(() => {
        if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      console.error("loadMessages failed:", e);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) loadThreads();
    else loadMessages();
    const t = setInterval(() => {
      if (isAdmin) { if (active) loadMessages(active); loadThreads(); }
      else loadMessages();
    }, 4000);
    return () => clearInterval(t);
  }, [isAdmin, active, loadMessages, loadThreads]);

  const send = async () => {
    const to = isAdmin ? active : "doctor";
    if (isAdmin && !active) return;
    if (!text.trim() && pending.length === 0) return;
    try {
      await api.post("/chat/messages", { to_user_id: to, content: text, attachments: pending.map((p) => ({ id: p.id })) });
      setText("");
      setPending([]);
      if (isAdmin) loadMessages(active); else loadMessages();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Message could not be sent");
    }
  };

  const attach = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files) {
      setBusy(file.name);
      try {
        const stored = file.type.startsWith("image/")
          ? await uploadFile(file, "chat", { maxWidth: 2200, quality: 0.9 })
          : file.size > 12 * 1024 * 1024
            ? await uploadVideoChunked(file, "chat")
            : await uploadFile(file, "chat", { raw: true });
        setPending((p) => [...p, { id: stored.id, name: file.name, content_type: stored.content_type, url: stored.url }]);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `${file.name} could not be attached`);
      } finally {
        setBusy("");
      }
    }
  };

  return (
    <main className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
      <div className="overline mb-2">Consultation</div>
      <h1 className="font-display text-4xl mb-8">Direct Chat</h1>

      <div className="grid lg:grid-cols-4 gap-6 h-[70vh]">
        {isAdmin && (
          <div className="lg:col-span-1 border border-border rounded-2xl overflow-hidden bg-card">
            <div className="p-4 border-b border-border font-display text-lg">Patients</div>
            <div className="overflow-y-auto">
              {threads.length === 0 && <p className="text-xs text-muted-foreground p-4">No conversations yet.</p>}
              {threads.map((t) => (
                <button key={t.user_id} onClick={()=>{setActive(t.user_id); loadMessages(t.user_id);}} data-testid={`thread-${t.user_id}`}
                  className={`w-full text-left p-3 border-b border-border flex items-center gap-3 hover:bg-accent ${active===t.user_id?"bg-accent":""}`}>
                  <Avatar className="h-9 w-9">
                    {t.profile_pic && <AvatarImage src={mediaUrl(t.profile_pic)}/>}
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">{t.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{t.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{t.last_message}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={`${isAdmin?"lg:col-span-3":"lg:col-span-4"} border border-border rounded-2xl flex flex-col overflow-hidden bg-card`}>
          {isAdmin && !active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
              <MessageCircle size={36} className="mb-3"/>
              <p className="text-sm">Select a patient to begin chatting.</p>
            </div>
          ) : (
            <>
              <div ref={scrollerRef} className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="chat-messages">
                {msgs.length === 0 && <p className="text-center text-muted-foreground text-sm">{isAdmin?"No messages yet.":"Start the conversation with your Vaidya."}</p>}
                {msgs.map((m) => (
                  <div key={m.id} className={`flex ${m.from_user_id === user.id ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${
                      m.from_user_id === user.id ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted rounded-bl-sm"}`}>
                      {(m.attachments || []).length > 0 && (
                        <div className="space-y-2 mb-2" data-testid={`chat-attachments-${m.id}`}>
                          {m.attachments.map((f) => (
                            <Attachment key={f.id} file={f} mine={m.from_user_id === user.id} />
                          ))}
                        </div>
                      )}
                      {m.content}
                      <div className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t border-border p-4 space-y-3">
                {(pending.length > 0 || busy) && (
                  <div className="flex flex-wrap gap-2" data-testid="chat-pending">
                    {pending.map((p) => (
                      <span key={p.id} className="inline-flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1.5 text-xs" data-testid={`chat-pending-${p.id}`}>
                        <Paperclip size={12} /> <span className="truncate max-w-[10rem]">{p.name}</span>
                        <button onClick={() => setPending((v) => v.filter((x) => x.id !== p.id))} data-testid={`chat-pending-remove-${p.id}`}>
                          <X size={12} className="opacity-60 hover:opacity-100" />
                        </button>
                      </span>
                    ))}
                    {busy && <span className="text-xs text-muted-foreground" data-testid="chat-uploading">Uploading {busy}…</span>}
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="h-10 w-10 shrink-0 rounded-full border border-border flex items-center justify-center cursor-pointer hover:border-primary/50 hover:text-primary transition-colors" title="Attach a photo or document">
                    <Paperclip size={15} />
                    <input
                      type="file"
                      multiple
                      accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                      className="hidden"
                      onChange={attach}
                      data-testid="chat-attach"
                    />
                  </label>
                  <Input value={text} onChange={(e)=>setText(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&send()}
                    placeholder="Type your message…" className="bg-transparent" data-testid="chat-input"/>
                  <Button onClick={send} disabled={!!busy} className="bg-primary text-primary-foreground" data-testid="chat-send">
                    <Send size={14}/>
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
